import { SPARK_DIRECTIONS, DIRECTION_DEG, type SparkDirection } from '@web/shared';
import { cn } from '@/lib/cn';

/** x/y offset (−1..1) for each direction, with the receiver facing up. */
const POS: Record<SparkDirection, [number, number]> = {
  front: [0, -1],
  front_right: [0.72, -0.72],
  right: [1, 0],
  back_right: [0.72, 0.72],
  back: [0, 1],
  back_left: [-0.72, 0.72],
  left: [-1, 0],
  front_left: [-0.72, -0.72],
};

interface CompassProps {
  /** Interactive picker mode. */
  value?: SparkDirection | null;
  onChange?: (d: SparkDirection) => void;
  /** Read-only: just point at this direction (receiver view). */
  show?: SparkDirection | null;
  className?: string;
}

/**
 * A little radar. Centre = the person being Sparked, facing up ("their front").
 * In picker mode the sender taps where *they* are standing. In show mode a
 * glowing 👀 marker sits where the admirer said they were.
 */
export function Compass({ value, onChange, show, className }: CompassProps) {
  const interactive = !!onChange;
  const R = 70; // px radius for markers

  return (
    <div
      className={cn(
        'relative mx-auto aspect-square w-44 rounded-full border-2 border-ink bg-flavor-melon-soft',
        className,
      )}
      role={interactive ? 'radiogroup' : undefined}
      aria-label={interactive ? 'Where are you standing?' : 'Direction the Spark came from'}
    >
      {/* crosshairs */}
      <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-ink/20" />
      <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-ink/20" />

      {/* centre = them, facing up */}
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
        <span className="block text-lg leading-none">🧍</span>
        <span className="block text-[0.55rem] font-semibold text-ink-soft">
          {show ? 'you' : 'them'} ↑
        </span>
      </span>

      {SPARK_DIRECTIONS.map((d) => {
        const [x, y] = POS[d];
        const active = interactive ? value === d : show === d;
        const style = {
          left: `calc(50% + ${x * R}px)`,
          top: `calc(50% + ${y * R}px)`,
        };
        if (interactive) {
          return (
            <button
              key={d}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={d.replace('_', ' ')}
              onClick={() => onChange!(d)}
              style={style}
              className={cn(
                'absolute size-7 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-ink transition-transform',
                active ? 'scale-125 bg-accent' : 'bg-surface hover:scale-110',
              )}
            />
          );
        }
        return (
          <span
            key={d}
            style={style}
            className={cn(
              'absolute -translate-x-1/2 -translate-y-1/2',
              active ? 'text-2xl' : 'size-2 rounded-full bg-ink/15',
            )}
          >
            {active ? '👀' : null}
          </span>
        );
      })}

      {show && (
        <span
          className="sonar-ring absolute size-10 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-accent"
          style={{
            left: `calc(50% + ${POS[show][0] * R}px)`,
            top: `calc(50% + ${POS[show][1] * R}px)`,
          }}
          aria-hidden
        />
      )}
      <span className="sr-only">{show ? `Admirer is at ${DIRECTION_DEG[show]}°` : ''}</span>
    </div>
  );
}
